// Mock data for Ankit Ojha's DevSecOps Portfolio

export const personalInfo = {
  name: "Ankit Ojha",
  title: "DevSecOps Consultant",
  tagline: "Architecting Secure Cloud Infrastructure & Driving Innovation",
  location: "Kathmandu, Nepal",
  email: "theankitojha@gmail.com",
  phone: "+977-9865606400",
  github: "https://github.com/theankitojha",
  linkedin: "https://linkedin.com/in/theankitojha",
  medium: "https://theankitojha.medium.com"
};

export const metrics = [
  { label: "AWS Cost Reduction", value: "40%", icon: "DollarSign" },
  { label: "Work Efficiency Boost", value: "50%", icon: "TrendingUp" },
  { label: "Release Speed Increase", value: "20%", icon: "Zap" },
  { label: "Code Security Boost", value: "25%", icon: "Shield" },
  { label: "Banks Onboarded", value: "7", icon: "Building" },
  { label: "Years of Experience", value: "4+", icon: "Clock" }
];

export const skills = [
  // Languages & Scripting
  { name: "Bash", category: "Language", icon: "Terminal", project: "Automated deployment scripts" },
  { name: "Python", category: "Language", icon: "Code", project: "Infrastructure automation tools" },
  
  // Version Control
  { name: "Git", category: "Version Control", icon: "GitBranch", project: "Code versioning & collaboration" },
  { name: "GitHub", category: "Version Control", icon: "Github", project: "CI/CD pipelines with Actions" },
  { name: "GitLab", category: "Version Control", icon: "GitlabIcon", project: "GitLab CI for automated testing" },
  { name: "Bitbucket", category: "Version Control", icon: "Binary", project: "Repository management" },
  
  // Containerization & Orchestration
  { name: "Docker", category: "Container", icon: "Container", project: "Containerized legacy applications" },
  { name: "Kubernetes", category: "Orchestration", icon: "Box", project: "Managed bank systems on Rancher K8s" },
  { name: "Rancher", category: "Orchestration", icon: "Layers", project: "Mobile banking infrastructure" },
  
  // CI/CD
  { name: "Jenkins", category: "CI/CD", icon: "Workflow", project: "Smart CI/CD pipelines" },
  { name: "GitHub Actions", category: "CI/CD", icon: "PlayCircle", project: "Automated testing & deployment" },
  { name: "GitLab CI", category: "CI/CD", icon: "GitCommit", project: "Continuous integration workflows" },
  { name: "ArgoCD", category: "GitOps", icon: "RefreshCw", project: "GitOps workflows for K8s" },
  
  // Cloud Providers
  { name: "AWS", category: "Cloud", icon: "Cloud", project: "Infrastructure on EC2, S3, RDS" },
  { name: "Azure", category: "Cloud", icon: "CloudCog", project: "Cloud infrastructure management" },
  { name: "DigitalOcean", category: "Cloud", icon: "CloudLightning", project: "Scalable droplet deployments" },
  
  // Infrastructure as Code
  { name: "Terraform", category: "IaC", icon: "FileCode", project: "AWS infrastructure automation" },
  { name: "Ansible", category: "IaC", icon: "Settings", project: "Configuration management" },
  { name: "Salt Stack", category: "IaC", icon: "Server", project: "Infrastructure orchestration" },
  
  // Databases
  { name: "MySQL", category: "Database", icon: "Database", project: "Optimized AWS RDS performance" },
  { name: "MongoDB", category: "Database", icon: "DatabaseZap", project: "NoSQL data storage" },
  { name: "Redis", category: "Database", icon: "Cpu", project: "Caching & session management" },
  
  // Monitoring & Logging
  { name: "Prometheus", category: "Monitoring", icon: "ActivitySquare", project: "Metrics collection" },
  { name: "Grafana", category: "Monitoring", icon: "BarChart3", project: "Real-time dashboards" },
  { name: "EFK Stack", category: "Logging", icon: "FileText", project: "Centralized logging system" },
  { name: "ELK Stack", category: "Logging", icon: "Search", project: "Log analysis & monitoring" },
  { name: "CloudWatch", category: "Monitoring", icon: "Eye", project: "AWS infrastructure monitoring" },
  
  // Security
  { name: "OWASP", category: "Security", icon: "ShieldCheck", project: "Security scanning in CI/CD" },
  { name: "SonarQube", category: "Security", icon: "ShieldAlert", project: "Code security analysis" },
  { name: "Bitwarden", category: "Security", icon: "Key", project: "Secrets management" },
  { name: "LDAP", category: "Security", icon: "Lock", project: "Centralized authentication" },
  { name: "MFA", category: "Security", icon: "Smartphone", project: "Multi-factor authentication" },
  
  // Web Servers
  { name: "NGINX", category: "Web Server", icon: "Globe", project: "Reverse proxy & load balancing" },
  { name: "Apache", category: "Web Server", icon: "ServerCog", project: "Web server configuration" }
];

export const experience = [
  {
    id: 5,
    company: "Goalprize",
    role: "DevSecOps Consultant",
    period: "Sept 2024 - Present",
    location: "Remote",
    achievements: [
      "Reduced cloud bills by 40% on AWS by managing and architecting infrastructure through right-sizing instances",
      "Built secure CI/CD pipelines with OWASP scanning and SonarQube, boosting code security by 25%",
      "Implemented GitOps workflows with ArgoCD to automate declarative Kubernetes deployments",
      "Deployed EFK stack for centralized logging, enabling real-time monitoring and analysis of application logs",
      "Configured DNS security to reduce DDoS attacks, IP filtering to sensitive domains, and security rules for domains"
    ],
    status: "success",
    buildNumber: "#2024.09"
  },
  {
    id: 4,
    company: "Cotiviti",
    role: "Software Engineer (DevOps)",
    period: "Mar 2023 - Sept 2024",
    location: "Remote",
    achievements: [
      "Helped speed up the release process by 20% by setting up smart CI/CD pipelines",
      "Migrated legacy applications to containerized environments, which helped boost work efficiency by over 50%",
      "Integrated security scanning tools within CI/CD for code assessment and vulnerability checks",
      "Optimized database performance on AWS RDS by implementing automated backups and scaling techniques",
      "Coordinated security audits to align infrastructures with industry standards like HIPAA"
    ],
    status: "success",
    buildNumber: "#2023.03"
  },
  {
    id: 3,
    company: "AMNIL Technologies",
    role: "Associate DevOps Engineer",
    period: "Sept 2022 - Jan 2023",
    location: "Kathmandu, Nepal",
    achievements: [
      "Onboarded 7 banks in a span of 5 months to production, significantly boosting profitability of the company",
      "Actively involved in centralizing authentications with LDAP and MFA for access control to align with ISO27001 standard",
      "Performed server hardening, VA-PT analysis, and consistently monitored the uptime of servers",
      "Established robust secrets management with tools like Bitwarden, mitigating risks of unauthorized access to sensitive information"
    ],
    status: "success",
    buildNumber: "#2022.09"
  },
  {
    id: 2,
    company: "Bitsbeat IT Solutions",
    role: "Junior DevOps Engineer",
    period: "Sept 2021 - Aug 2022",
    location: "Kathmandu, Nepal",
    achievements: [
      "Assisted senior team members with AWS infrastructure setup (EC2, S3, ELB, VPC), in deploying scalable solutions",
      "Actively managed, improved, and monitored cloud infrastructure on AWS using CloudWatch"
    ],
    status: "success",
    buildNumber: "#2021.09"
  },
  {
    id: 1,
    company: "F1soft International",
    role: "Support Trainee",
    period: "Mar 2021 - Aug 2021",
    location: "Kathmandu, Nepal",
    achievements: [
      "Handled mobile banking system (Banksmart) of more than a dozen of banks in Nepal, on Rancher Kubernetes"
    ],
    status: "success",
    buildNumber: "#2021.03"
  }
];

export const projects = [
  {
    id: 1,
    title: "Nagarik News Portal",
    description: "DevOps infrastructure for Nepal's largest news portal, handling millions of daily visitors with high availability and scalability.",
    technologies: ["Kubernetes", "Docker", "AWS", "NGINX", "Monitoring"],
    link: "https://nagariknews.nagariknetwork.com/",
    company: "AMNIL Technologies",
    impact: "99.9% uptime for millions of daily users",
    scanStatus: "PASSED",
    vulnerabilities: 0,
    securityScore: 98
  },
  {
    id: 2,
    title: "Secure CI/CD Pipeline with Security Scanning",
    description: "Built enterprise-grade CI/CD pipelines with integrated OWASP and SonarQube scanning, achieving 25% improvement in code security.",
    technologies: ["Jenkins", "OWASP", "SonarQube", "Docker", "GitOps"],
    company: "Goalprize",
    impact: "25% boost in code security",
    scanStatus: "PASSED",
    vulnerabilities: 0,
    securityScore: 95
  },
  {
    id: 3,
    title: "Cloud Cost Optimization Framework",
    description: "Architected and implemented AWS infrastructure optimization strategies, reducing cloud costs by 40% through right-sizing and automation.",
    technologies: ["AWS", "Terraform", "CloudWatch", "Python", "Cost Explorer"],
    company: "Goalprize",
    impact: "40% reduction in AWS costs",
    scanStatus: "PASSED",
    vulnerabilities: 0,
    securityScore: 92
  },
  {
    id: 4,
    title: "Banking Infrastructure with ISO27001 Compliance",
    description: "Onboarded 7 banks to production with centralized LDAP authentication, MFA, and comprehensive security hardening aligned with ISO27001.",
    technologies: ["Kubernetes", "LDAP", "MFA", "Bitwarden", "Security Auditing"],
    company: "AMNIL Technologies",
    impact: "7 banks onboarded in 5 months",
    scanStatus: "PASSED",
    vulnerabilities: 0,
    securityScore: 97
  }
];

export const education = {
  degree: "Bachelor of Engineering in Computer Engineering",
  university: "Tribhuvan University",
  period: "2016 - 2020",
  gpa: "3.29/4.00"
};

export const interests = [
  "Neural Networks",
  "Deep Learning",
  "AI in Cybersecurity",
  "Cloud Architecture",
  "Infrastructure Automation"
];
